import React from 'react';
import Icon from 'components/AppIcon';
import EmptyState from 'components/EmptyState';
import Tooltip from 'components/Tooltip';

import BatchStatusBadge from './BatchStatusBadge';


const SORT_FIELDS = ['batchId', 'metal', 'purchaseDate', 'status', 'expectedMargin', 'progress'];

export default function BatchTable({
  batches,
  selectedIds,
  onSelectAll,
  onSelectOne,
  sortField,
  sortDir,
  onSort,
  onViewDetail,
  onEditBatch,
  onStatusChange,
  onDeleteBatch,
  onViewTimeline,
  flashIds = new Set(),
  emptyVariant = 'no-batches', // 'no-batches' | 'filtered' | 'search'
  onClearFilter,
  onClearSearch,
  onNavigateCalculator,
}) {
  const allSelected = batches?.length > 0 && selectedIds?.length === batches?.length;
  const someSelected = selectedIds?.length > 0 && !allSelected;

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <Icon name="ChevronsUpDown" size={13} color="var(--color-muted-foreground)" />;
    return sortDir === 'asc'
      ? <Icon name="ChevronUp" size={13} color="var(--color-accent)" />
      : <Icon name="ChevronDown" size={13} color="var(--color-accent)" />;
  };

  const Th = ({ field, label, className = '', tooltip }) => (
    <th
      className={`px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide cursor-pointer select-none hover:text-slate-700 whitespace-nowrap ${className}`}
      style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {tooltip ? (
          <Tooltip description={tooltip}><span>{label}</span></Tooltip>
        ) : label}
        {' '}<SortIcon field={field} />
      </span>
    </th>
  );

  const ThStatic = ({ label, className = '', tooltip }) => (
    <th className={`px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap ${className}`}
      style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
      {tooltip ? (
        <Tooltip description={tooltip}><span>{label}</span></Tooltip>
      ) : label}
    </th>
  );

  const getEmptyState = () => {
    if (emptyVariant === 'search') {
      return (
        <EmptyState
          icon="Search"
          title="Sin resultados"
          description="Ningún lote coincide con tu búsqueda."
          ctaLabel="Limpiar búsqueda"
          ctaAction={onClearSearch}
          colSpan={11}
        />
      );
    }
    if (emptyVariant === 'filtered') {
      return (
        <EmptyState
          icon="Filter"
          title="Sin resultados para este estado"
          description="No hay lotes con el estado seleccionado. Prueba con otro filtro."
          ctaLabel="Ver todos los lotes"
          ctaAction={onClearFilter}
          colSpan={11}
        />
      );
    }
    // default: no-batches
    return (
      <EmptyState
        icon="ClipboardList"
        title="Sin lotes activos"
        description="Aún no has creado ningún lote. Empieza calculando el precio máximo de compra."
        ctaLabel="Ir a Calculadora"
        ctaAction={onNavigateCalculator}
        colSpan={11}
      />
    );
  };

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-tour="batch-table">
      <table className="w-full min-w-[1100px]">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="px-3 py-3 w-10">
              <input
                type="checkbox"
                checked={allSelected}
                ref={el => { if (el) el.indeterminate = someSelected; }}
                onChange={e => onSelectAll(e?.target?.checked)}
                className="w-4 h-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
              />
            </th>
            <Th field="batchId" label="ID Lote" tooltip="Código único identificador del lote. Generado automáticamente al crear el lote desde la calculadora." />
            <Th field="metal" label="Metal / Cat." tooltip="Tipo de metal (ej: Cobre, Aluminio) y categoría de material del lote." />
            <ThStatic label="Peso (t)" tooltip="Peso total del lote expresado en toneladas métricas." />
            <ThStatic label="Yield Est. (%)" tooltip="Rendimiento estimado del lote: porcentaje del peso bruto que se recuperará como metal puro tras el procesamiento." />
            <ThStatic label="Yield Real (%)" tooltip="Rendimiento real registrado del lote tras el procesamiento." />
            <ThStatic label="Precio Máx (€/t)" tooltip="Precio máximo de compra calculado por tonelada para que el lote sea rentable, según los parámetros de la calculadora." />
            <Th field="expectedMargin" label="Margen Esp." tooltip="Margen de beneficio esperado sobre el precio de venta, expresado en porcentaje." />
            <Th field="status" label="Estado" tooltip="Estado actual del lote en el flujo de trabajo: En Espera, Procesando, Listo para Venta o Completado." />
            <ThStatic label="Días Inv." tooltip="Número de días que el lote lleva en inventario desde su creación. Rojo = más de 30 días, Ámbar = más de 14 días." />
            <Th field="purchaseDate" label="Creado" tooltip="Fecha en que el lote fue registrado en el sistema." />
            <ThStatic label="Acciones" className="text-right" tooltip="Acciones disponibles para este lote: ver detalle, editar, cambiar estado y eliminar." />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {batches?.length === 0 ? (
            getEmptyState()
          ) : batches?.map(batch => {
            const isSelected = selectedIds?.includes(batch?.id);
            const isFlashing = flashIds?.has(batch?.id);
            return (
              <tr
                key={batch?.id}
                className={`transition-colors ${
                  isFlashing
                    ? 'bg-orange-50 ring-1 ring-orange-300'
                    : isSelected
                    ? 'bg-orange-50' : 'hover:bg-slate-50'
                }`}
                style={isFlashing ? { transition: 'background-color 0.3s ease, box-shadow 0.3s ease' } : {}}
              >
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={e => onSelectOne(batch?.id, e?.target?.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                  />
                </td>
                <td className="px-3 py-3">
                  <span className="font-mono text-sm font-semibold text-slate-800">{batch?.batchId}</span>
                </td>
                <td className="px-3 py-3">
                  <div>
                    <p className="text-sm font-medium text-slate-800" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{batch?.metal}</p>
                    <p className="text-xs text-slate-500" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{batch?.category}</p>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <span className="text-sm font-mono text-slate-700">
                    {batch?.weightT != null ? batch?.weightT?.toFixed(3) : '—'}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <span className="text-sm font-mono text-slate-700">
                    {batch?.yieldEstimated != null ? batch?.yieldEstimated?.toFixed(1) + '%' : '—'}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <span className="text-sm font-mono text-slate-700">
                    {batch?.yieldActual != null ? batch?.yieldActual?.toFixed(1) + '%' : '—'}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <span className="text-sm font-mono text-slate-700">
                    {batch?.maxBuyPrice != null ? batch?.maxBuyPrice?.toLocaleString('es-ES', { maximumFractionDigits: 0 }) + ' €' : '—'}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <span className="text-sm font-medium text-slate-700 font-mono">
                    {batch?.expectedMargin != null ? batch?.expectedMargin?.toFixed(1) + '%' : '—'}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <BatchStatusBadge status={batch?.status} />
                </td>
                <td className="px-3 py-3">
                  {batch?.daysInInventory != null ? (
                    <span className={`text-sm font-mono ${
                      batch?.daysInInventory > 30 ? 'text-red-600 font-semibold' :
                      batch?.daysInInventory > 14 ? 'text-amber-600' : 'text-slate-600'
                    }`}>
                      {batch?.daysInInventory}d
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <span className="text-sm text-slate-600" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{batch?.purchaseDate}</span>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <Tooltip description="Ver el detalle completo del lote: métricas, costes, notas y estado.">
                      <button
                        onClick={() => onViewDetail(batch)}
                        className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        title="Ver detalle"
                      >
                        <Icon name="Eye" size={15} color="currentColor" />
                      </button>
                    </Tooltip>
                    {onViewTimeline && (
                      <Tooltip description="Ver el historial de cambios de estado del lote con fechas y notas de cada transición.">
                        <button
                          onClick={() => onViewTimeline(batch)}
                          data-tour="batch-timeline-btn"
                          className="p-1.5 rounded-md text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-colors"
                          title="Ver historial"
                        >
                          <Icon name="History" size={15} color="currentColor" />
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip description="Editar los datos del lote: peso, precio, yield estimado, costes logísticos y notas.">
                      <button
                        onClick={() => onEditBatch(batch)}
                        className="p-1.5 rounded-md text-slate-400 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                        title="Editar lote"
                      >
                        <Icon name="Pencil" size={15} color="currentColor" />
                      </button>
                    </Tooltip>
                    <Tooltip description="Cambiar el estado del lote: mover de En Espera → Procesando → Listo para Venta → Completado.">
                      <button
                        onClick={() => onStatusChange(batch)}
                        className="p-1.5 rounded-md text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                        title="Cambiar estado"
                      >
                        <Icon name="RefreshCw" size={15} color="currentColor" />
                      </button>
                    </Tooltip>
                    {onDeleteBatch && (
                      <Tooltip description="Eliminar permanentemente este lote. Esta acción no se puede deshacer.">
                        <button
                          onClick={() => onDeleteBatch(batch)}
                          className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Eliminar lote"
                        >
                          <Icon name="Trash2" size={15} color="currentColor" />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}